import { forwardRef } from 'react';
import moment from 'moment';
import {
  EMPTY_RANGE,
  HourRange,
  formatDurationFromMinutes,
  formatTime,
} from './HourRangePicker';
import { useStyles } from './ScheduleExportStyles';
import { Locale, translate } from '../../i18n/translations';

const PERIODS = ['morning', 'intermediary', 'afternoon'] as const;

type Period = (typeof PERIODS)[number];

type ScheduleExportProps = {
  people: string[];
  days: moment.Moment[];
  shifts: Record<string, HourRange>;
  locale: Locale;
};

type Assignment = {
  person: string;
  startMinutes: number;
  endMinutes: number;
};

const WEEKDAY_WINDOWS: Record<Period, { startMinutes: number; endMinutes: number }> =
  {
    morning: { startMinutes: 8 * 60, endMinutes: 17 * 60 },
    intermediary: { startMinutes: 11 * 60, endMinutes: 19 * 60 },
    afternoon: { startMinutes: 13 * 60, endMinutes: 22 * 60 },
  };

const getShiftKey = (person: string, dayKey: string) => `${person}-${dayKey}`;

const getPeriodWindows = (day: moment.Moment) => {
  if (day.isoWeekday() !== 7) return WEEKDAY_WINDOWS;

  return {
    ...WEEKDAY_WINDOWS,
    afternoon: { startMinutes: 12 * 60, endMinutes: 20 * 60 },
  };
};

const getShiftPeriod = (range: HourRange, day: moment.Moment): Period | null => {
  if (range.mark || range.startMinutes === null || range.endMinutes === null) {
    return null;
  }

  const start = range.startMinutes;
  const end = range.endMinutes;
  const windows = getPeriodWindows(day);
  let closest: Period = 'morning';
  let closestDistance = Number.POSITIVE_INFINITY;

  PERIODS.forEach((period) => {
    const window = windows[period];
    const distance =
      Math.abs(start - window.startMinutes) +
      Math.abs(end - window.endMinutes);

    if (distance < closestDistance) {
      closest = period;
      closestDistance = distance;
    }
  });

  return closest;
};

const formatLocalized = (
  day: moment.Moment,
  locale: Locale,
  format: string,
) => day.clone().locale(locale).format(format);

const capitalize = (value: string, locale: Locale) =>
  value.charAt(0).toLocaleUpperCase(locale) + value.slice(1);

const formatWeekTitle = (days: moment.Moment[], locale: Locale) => {
  const start = days[0];
  const end = days[days.length - 1];

  if (start.month() === end.month() && start.year() === end.year()) {
    return translate(locale, 'exportTitleSameMonth', {
      start: formatLocalized(start, locale, 'D'),
      end: formatLocalized(end, locale, 'D'),
      monthYear: formatLocalized(start, locale, 'MMMM YYYY'),
    });
  }

  if (start.year() === end.year()) {
    return translate(locale, 'exportTitleRange', {
      start: formatLocalized(start, locale, 'D MMMM'),
      end: formatLocalized(end, locale, 'D MMMM YYYY'),
    });
  }

  return translate(locale, 'exportTitleRange', {
    start: formatLocalized(start, locale, 'D MMMM YYYY'),
    end: formatLocalized(end, locale, 'D MMMM YYYY'),
  });
};

const formatAssignments = (assignments: Assignment[]) => {
  if (assignments.length === 0) return '—';

  return assignments
    .map(
      (item) =>
        `${item.person} ${formatTime(item.startMinutes)}–${formatTime(item.endMinutes)} (${formatDurationFromMinutes(item.endMinutes - item.startMinutes)})`,
    )
    .join(', ');
};

const ScheduleExport = forwardRef<HTMLDivElement, ScheduleExportProps>(
  ({ people, days, shifts, locale }, ref) => {
    const classes = useStyles();

    const getAssignments = (day: moment.Moment, period: Period): Assignment[] => {
      const dayKey = day.format('YYYY-MM-DD');

      return people.flatMap((person) => {
        const range = shifts[getShiftKey(person, dayKey)] ?? EMPTY_RANGE;
        if (
          getShiftPeriod(range, day) !== period ||
          range.startMinutes === null ||
          range.endMinutes === null
        ) {
          return [];
        }

        return [
          {
            person,
            startMinutes: range.startMinutes,
            endMinutes: range.endMinutes,
          },
        ];
      });
    };

    return (
      <div className={classes.offscreen} aria-hidden>
        <div ref={ref} className={classes.page}>
          <h1 className={classes.title}>{formatWeekTitle(days, locale)}</h1>
          <div className={classes.days}>
            {days.map((day) => (
              <section key={day.format('YYYY-MM-DD')} className={classes.day}>
                <h2 className={classes.dayHeading}>
                  {capitalize(formatLocalized(day, locale, 'dddd'), locale)}{' '}
                  {formatLocalized(day, locale, 'D')}
                </h2>
                {PERIODS.map((period) => {
                  const assignments = getAssignments(day, period);
                  if (assignments.length === 0) return null;

                  return (
                    <p key={period} className={classes.period}>
                      <span className={classes.periodLabel}>
                        {`${translate(locale, period)}: `}
                      </span>
                      {formatAssignments(assignments)}
                    </p>
                  );
                })}
              </section>
            ))}
          </div>
        </div>
      </div>
    );
  },
);

ScheduleExport.displayName = 'ScheduleExport';

export default ScheduleExport;
